/**
 * 確認の画面の「見える所」の英語（2026-10-03 console/plain-confirm・founder「一般のユーザーだったら、まじでいみわからん」）。
 * en.ts が `...enConfirmPlain` で取り込む（ja.confirmPlain.ts が同じキーを全部持つことを型で強制）。
 *
 * ここのキーは全部、ボタンより上（見出し・結果・安全の 1 行）か、ボタンそのものに出る = 中の言葉を書かない
 * （confirmPlain.ts PLAIN_FORBIDDEN・confirmPlain.test.ts が全部の値を確かめる）。中の言葉は「Details」（confirmAction.* の文）へ。
 * 決め事: 見出しは「〜しますか？」の 1 文・結果は目に見えることを 1 文・言い方は利用者の言葉（publish / people / your game）。
 * 2 回目（2026-10-04 console/plain-confirm-2）: ja と同じ意味で、短く自然な英語に（ja.confirmPlain.ts の冒頭）。
 * {name} = プロジェクトの名前（利用者が付けた値）。{url} = 本体が返した公開先（本物の URL）。名前の無い時は …_noName の文。
 */
export const enConfirmPlain = {
  'confirmPlain.pageTitle': 'Please confirm',
  'confirmPlain.loading': 'Loading',
  'confirmPlain.cancel': 'Cancel',
  'confirmPlain.safety': "If this doesn't look familiar, press Cancel. Nothing will change.",
  'confirmPlain.safetyCli': "If this doesn't look familiar, press Cancel. Nothing will change.",
  'confirmPlain.details': 'Details',
  'confirmPlain.stopsGame': 'No one can open it until you publish it again.',
  'confirmPlain.alsoOn': 'This also turns on {list}.',
  // 公開と同じ 1 回でいっしょにすること（2026-10-08 core/one-confirm・confirmPlain.ts PlainSummary.alsoDo）。名詞の形で並べる
  'confirmPlain.alsoDo': 'This also includes {list}.',
  'confirmPlain.alsoDo.serverOn': 'starting your app server',
  'confirmPlain.alsoDo.serverUpdate': 'updating your app server',
  'confirmPlain.alsoDo.settings': 'changing the app settings',
  'confirmPlain.alsoDo.dataShape': 'changing how your app stores its data',
  'confirmPlain.alsoDo.domain': 'adding your own web address',
  // 公開の費用の目安（2026-10-04 money/publish-cost・confirmPlain.ts costOf）。結果の行の下に小さく
  'confirmPlain.cost': 'Estimated cost: {storage} a month to store, {per10k} per 10,000 visits.',
  'confirmPlain.otherChanges': 'There are other changes too.',
  // 文と文の間（en は空白・ja は無し）
  'confirmPlain.sentenceGap': ' ',
  // 並べ方: "saves, scores and multiplayer"（最後の 2 つの間だけ listLast）
  'confirmPlain.listJoin': ', ',
  'confirmPlain.listLast': ' and ',
  // 表の名前を並べる（"scores", "users"）。前後の " は文の側
  'confirmPlain.tableJoin': '", "',
  'confirmPlain.feature.saves': 'saves',
  'confirmPlain.feature.stats': 'scores',
  'confirmPlain.feature.multiplayer': 'multiplayer',
  'confirmPlain.feature.emailLogin': 'sign-in with email',
  'confirmPlain.feature.googleLogin': 'sign-in with Google',
  'confirmPlain.feature.discordLogin': 'sign-in with Discord',
  'confirmPlain.feature.appleLogin': 'sign-in with Apple',
  'confirmPlain.feature.xLogin': 'sign-in with X',
  'confirmPlain.feature.realtimeChannels': 'real-time messaging',
  'confirmPlain.feature.voice': 'voice calls',
  'confirmPlain.feature.p2p': 'direct device-to-device connections',
  'confirmPlain.feature.other': 'other features it needs',

  // 見出し（〜しますか？）
  'confirmPlain.headline.publish': 'Publish "{name}"?',
  'confirmPlain.headline.publish_noName': 'Publish your app?',
  // テスト用（development）への公開の確認（2026-10-08 publish-url）。「version」は中の言葉（PLAIN_FORBIDDEN）= ダッシュボードの「test app」で言う
  'confirmPlain.headline.publishTest': 'Publish "{name}" to your test app?',
  'confirmPlain.headline.publishTest_noName': 'Publish your app to your test app?',
  'confirmPlain.headline.allowPublish': 'Get "{name}" ready to publish?',
  'confirmPlain.headline.allowPublish_noName': 'Get your app ready to publish?',
  'confirmPlain.headline.stop': 'Unpublish "{name}"?',
  'confirmPlain.headline.stop_noName': 'Unpublish your app?',
  'confirmPlain.headline.turnOn': 'Add new features to "{name}"?',
  'confirmPlain.headline.turnOn_noName': 'Add new features to your app?',
  'confirmPlain.headline.settings': 'Change the settings of "{name}"?',
  'confirmPlain.headline.settings_noName': 'Change your app settings?',
  'confirmPlain.headline.domainAdd': 'Make "{name}" open at {hostname} too?',
  'confirmPlain.headline.domainAdd_noName': 'Make your app open at {hostname} too?',
  'confirmPlain.headline.domainRemove': 'Stop "{name}" opening at {hostname}?',
  'confirmPlain.headline.domainRemove_noName': 'Stop your app opening at {hostname}?',
  'confirmPlain.headline.serverOn': 'Start the server for "{name}"?',
  'confirmPlain.headline.serverOn_noName': 'Start your app server?',
  'confirmPlain.headline.serverOff': 'Stop the server for "{name}"?',
  'confirmPlain.headline.serverOff_noName': 'Stop your app server?',
  'confirmPlain.headline.serverDelete': 'Delete the server for "{name}"?',
  'confirmPlain.headline.serverDelete_noName': 'Delete your app server?',
  'confirmPlain.headline.serverPurge': 'Delete the server for "{name}" and all its saved data?',
  'confirmPlain.headline.serverPurge_noName': 'Delete your app server and all its saved data?',
  'confirmPlain.headline.serverUpdate': 'Update the server for "{name}"?',
  'confirmPlain.headline.serverUpdate_noName': 'Update your app server?',
  'confirmPlain.headline.dataChange': 'Change the data of "{name}"?',
  'confirmPlain.headline.dataChange_noName': 'Change your app data?',
  'confirmPlain.headline.dataRead': 'Read the data of "{name}"?',
  'confirmPlain.headline.dataRead_noName': 'Read your app data?',
  'confirmPlain.headline.dataDelete': 'Delete data from "{name}"?',
  'confirmPlain.headline.dataDelete_noName': 'Delete data from your app?',
  'confirmPlain.headline.dataAdd': 'Add data to "{name}"?',
  'confirmPlain.headline.dataAdd_noName': 'Add data to your app?',
  'confirmPlain.headline.dataShape': 'Change how "{name}" stores its data?',
  'confirmPlain.headline.dataShape_noName': 'Change how your app stores its data?',
  'confirmPlain.headline.other': 'Make a change to "{name}"?',
  'confirmPlain.headline.other_noName': 'Make a change to your app?',
  'confirmPlain.headline.ban': 'Suspend {playerId}?',
  'confirmPlain.headline.unban': 'Lift the suspension of {playerId}?',
  'confirmPlain.headline.appealAccept': 'Agree to lift the suspension?',
  'confirmPlain.headline.appealReject': 'Decline to lift the suspension?',
  'confirmPlain.headline.secretPut': 'Save {secret}?',
  'confirmPlain.headline.secretDelete': 'Delete {secret}?',
  'confirmPlain.headline.mailSchedule': 'Schedule the email "{subject}"?',
  'confirmPlain.headline.mailRepeat': 'Send the email "{subject}" regularly?',
  'confirmPlain.headline.mailSend': 'Send the email "{subject}" now?',

  // 結果（目に見えること 1 文）
  'confirmPlain.result.publish': 'Anyone can open it at {url}.',
  'confirmPlain.result.publish_noUrl': 'Anyone can open it.',
  'confirmPlain.result.publishTest': 'You can try it at {url}.',
  'confirmPlain.result.publishTest_noUrl': 'You can try it on your test app.',
  // 押した後・もう一度開いたとき（2026-10-08 publish-url・founder「公開したときに URL 出してあげると親切かも」）。{url} は新しいタブで開くリンク。
  // approved は「まもなく」= 公開そのものは AI の送り直しで起きる（承認は送り直しを 1 回通す印）
  // 2026-10-13 core/confirm-flow: 「閉じてかまいません」をやめた（次にすることは confirmAction.next.* の 1 行・pendingAction.ts nextStepKey）
  // 公開されたかの 1 行（ページの一番上・2026-10-13 console/confirm-wait・confirmPlain.ts publishStateLine の WHY）。
  // 押す前は「押すまで公開されない」をはっきり（以前はページが開いただけで終わったと読まれた）。approvedChat の「continues」は confirmAction.next.chat と同じ流れ
  // 2026-10-15 console/confirm-publishing: approved の「Not published yet」をやめた（founder「不安だね」）。公開の確認の押した後は publishing.* の画面（confirmPublishing.ts）
  'confirmPlain.state.notYet': 'Not published yet. Press the button below to publish.',
  'confirmPlain.state.notYetTest': 'Not published to your test app yet. Press the button below to publish.',
  'confirmPlain.state.approvedChat': 'Getting ready to go live. It goes live when your AI continues.',
  'confirmPlain.state.approvedCli': 'Getting ready to go live. This usually takes a few seconds.',
  'confirmPlain.state.published': 'Published.',
  'confirmPlain.state.publishedTest': 'Published to your test app.',
  'confirmPlain.state.expired': 'Not published. This link has expired.',
  'confirmPlain.state.cancelled': 'Not published. You cancelled it.',
  'confirmPlain.state.notPublished': 'Not published.',
  'confirmPlain.done.publish': 'Approved. Your app will be live at {url} in a moment.',
  'confirmPlain.done.publishTest': 'Approved. Your test app will be at {url} in a moment.',
  'confirmPlain.done.published': 'This is done. Your app is live at {url}.',
  'confirmPlain.done.publishedTest': 'This is done. Your test app is at {url}.',
  // 押した後の見せ方（2026-10-15 console/confirm-publishing・founder「パブリッシュした後に『Not published yet』が出てくると不安」・confirmPublishing.ts の WHY）。
  // working = 準備中（動くバー）/ done = 公開しました + 開くボタン / stopped = 公開されなかった + 次の手。CLI は「CLI」と言わず {cliName}（PLAIN_FORBIDDEN）
  'confirmPlain.publishing.title': 'Getting your app ready to go live',
  'confirmPlain.publishing.titleTest': 'Getting your test app ready',
  'confirmPlain.publishing.waitCli': '{cliName} on your computer is finishing the publish. This usually takes a few seconds.',
  'confirmPlain.publishing.waitCliSlow': '{cliName} on your computer is still finishing the publish.',
  'confirmPlain.publishing.waitChat': 'It goes live when your AI continues with the next step.',
  'confirmPlain.publishing.where': 'It will be at {url}',
  'confirmPlain.publishing.leaveOpen': 'Keep this page open and it changes when it is done. You can also close it.',
  'confirmPlain.publishing.slowCli': 'This is taking longer than usual. Look at the window where {cliName} is running for an error.',
  'confirmPlain.publishing.unknownTitle': 'No word back yet',
  'confirmPlain.publishing.unknown': 'This page stopped checking after {minutes} minutes. Reload it to see where things are.',
  'confirmPlain.publishing.reload': 'Reload',
  'confirmPlain.published.title': 'Your app is live',
  'confirmPlain.published.titleTest': 'Your test app is ready',
  'confirmPlain.published.at': 'Anyone can open it at {url}.',
  'confirmPlain.published.atTest': 'You can try it at {url}.',
  'confirmPlain.published.noUrl': 'Anyone can open it now.',
  'confirmPlain.published.noUrlTest': 'You can try it on your test app now.',
  'confirmPlain.published.open': 'Open your app',
  'confirmPlain.published.openTest': 'Open your test app',
  'confirmPlain.stopped.title': 'It was not published',
  'confirmPlain.stopped.cancelledTitle': 'Cancelled',
  // CLI の人向け（confirmAction.status.expired / stale は「AI とのチャットに戻って」と言う = 合わない）。次の手 = 同じコマンドをもう一度
  'confirmPlain.stopped.expiredCli': 'Time ran out before it finished, so nothing was changed. To publish, run the same command on your computer again. A new page to confirm will open.',
  'confirmPlain.stopped.staleCli': 'Something changed after this page was made, so nothing was changed. To publish, run the same command on your computer again. A new page to confirm will open.',
  // CLI がもう待っていない（2026-10-15 core/confirm-done・10/15 Mega Strike・pendingAction.ts cliStoppedWaiting）。押しても何も起きない = ボタンを出さずに言う
  'confirmPlain.notWaiting.title': 'Pressing here would do nothing now',
  'confirmPlain.notWaiting.linePublish': '{cliName} on your computer stopped waiting for this page (it was stopped or its window was closed), so this page can no longer publish. Nothing was changed.',
  'confirmPlain.notWaiting.line': '{cliName} on your computer stopped waiting for this page (it was stopped or its window was closed), so this page can no longer make the change. Nothing was changed.',
  'confirmPlain.notWaiting.nextPublish': 'To publish, run the same command on your computer again and press the button on the new page that opens.',
  'confirmPlain.notWaiting.next': 'Run the same command on your computer again and press the button on the new page that opens.',
  'confirmPlain.stopped.notWaitingCli': '{cliName} on your computer stopped before it finished, so nothing was changed. To publish, run the same command on your computer again. A new page to confirm will open.',
  'confirmPlain.result.allowPublish': 'You can then publish it at {url}.',
  'confirmPlain.result.allowPublish_noUrl': 'You can then publish it.',
  'confirmPlain.result.stop': 'Opening {url} will show nothing.',
  'confirmPlain.result.stop_noUrl': 'Opening your app link will show nothing.',
  'confirmPlain.result.turnOn': 'People using it now will see it right away.',
  'confirmPlain.result.settings': 'People using it now will see it right away.',
  'confirmPlain.result.settingsInvisible': 'People using it will not notice any difference.',
  'confirmPlain.result.domainAdd': 'It opens there once you set it up where you bought the domain.',
  'confirmPlain.result.domainRemove': 'This address stops working right away.',
  'confirmPlain.result.serverDomainRemove': 'This address stops working right away, and someone else could take it.',
  'confirmPlain.result.serverOn': 'What you use is taken from your credit balance.',
  'confirmPlain.result.serverOff': 'Features that use the server will not work until you start it again.',
  'confirmPlain.result.serverDelete': 'Your saved data is kept.',
  'confirmPlain.result.serverPurge': 'Everything is deleted. This cannot be undone.',
  // テスト用（development）の確認の結果のあとに 1 行（2026-10-08 core/dev-confirm-links・confirmPlain.ts testApp）
  'confirmPlain.testOnly': 'This is only your test app. Your live app does not change.',
  // 公開の確認の「何が変わるか」（2026-10-25 security/prompt-lava-fixes の D・confirmPlain.ts PlainSummary.files / sameAsTest / hosts）。
  // WHY: 「公開しますか？」と URL だけでは、中の指示で入った悪いファイル・外へ送るコードを見分けられなかった（点検 2-⑥）
  'confirmPlain.files.first': 'This is the first time it goes public: {count} file(s).',
  'confirmPlain.files.same': 'The files are the same as what is live now.',
  'confirmPlain.files.changed': 'Compared with what is live now: {added} added, {changed} changed, {removed} removed.',
  'confirmPlain.files.addedLabel': 'Added:',
  'confirmPlain.files.changedLabel': 'Changed:',
  'confirmPlain.files.removedLabel': 'Removed:',
  'confirmPlain.files.more': '+{count} more',
  'confirmPlain.testLink': 'You can try it first on your test app: {url}',
  'confirmPlain.testSame': 'It has the same files as this.',
  'confirmPlain.testDiffers': 'Your test app has different files from these, so trying it there does not show what will go live.',
  'confirmPlain.hosts': "The server update contains these outside addresses: {list}. If you don't know one, press Cancel.",
  'confirmPlain.hostsMore': ' and {count} more',
  'confirmPlain.result.serverDeleteInvalid': 'This was asked in a way that does not work, so nothing will happen.',
  'confirmPlain.result.serverUpdate': 'People using it now will see it right away.',
  'confirmPlain.result.secretPut': 'An important value such as a password or key. It is not shown here.',
  'confirmPlain.result.secretDelete': 'Features that use this value will stop working.',
  'confirmPlain.result.sqlRead': 'It only reads data. Nothing changes.',
  'confirmPlain.result.sqlSchema': 'Changes how "{tables}" is stored. Your saved data is kept.',
  'confirmPlain.result.sqlSchemaAny': 'Changes how data is stored. Your saved data is kept.',
  'confirmPlain.result.sqlInsert': 'Adds data to "{tables}".',
  'confirmPlain.result.sqlUpdate': 'Changes data in "{tables}". This cannot be undone.',
  'confirmPlain.result.sqlDelete': 'Deletes data from "{tables}". This cannot be undone.',
  'confirmPlain.result.sqlDropColumn': 'Deletes some fields of "{tables}" and what is in them. This cannot be undone.',
  'confirmPlain.result.sqlDrop': 'Deletes "{tables}" and everything in it. This cannot be undone.',
  'confirmPlain.result.sqlUnknown': "This page can't tell which data will change. If you're not sure, press Cancel and ask your AI what it will do.",
  'confirmPlain.result.dataShapeDropColumn': 'Some fields of "{tables}" and what is in them will be deleted. This cannot be undone.',
  'confirmPlain.result.dataShapeKeep': 'Your saved data is kept.',
  'confirmPlain.result.dataShapeDrop': '"{tables}" and everything in it will be deleted. This cannot be undone.',
  'confirmPlain.result.ban': 'They are signed out and cannot use it.',
  'confirmPlain.result.unban': 'They can use it again.',
  'confirmPlain.result.appealAccept': 'The suspension is lifted, and they get your reply.',
  'confirmPlain.result.appealReject': 'The suspension stays, and they get your reply.',
  'confirmPlain.result.mailSchedule': 'Your users get it at the time you chose.',
  'confirmPlain.result.mailRepeat': 'Your users get it on the schedule you chose.',
  'confirmPlain.result.mailSend': 'Your users get it right away. It cannot be taken back.',
  'confirmPlain.result.other': "This page can't tell what will change. If you're not sure, press Cancel and ask your AI what it will do.",
  'confirmPlain.result.andMore': 'It also makes {count} more change(s) at the same time.'
} as const;
